import { Divider } from '@material-ui/core';
import { userSelector } from 'common/globalComponents/selectors';
import Breadcrumbs from 'components/Common/Breadcrumb';
import Loader from 'components/Loader';
import Paginate from 'components/Paginate';
import TableError from 'components/TableError';
import useRequest from 'hooks/useRequest';
import { useRouter } from 'hooks/useRouter';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import { createStructuredSelector } from 'reselect';
import { ACTIVE_AND_DELETED_ARRAY, GENDER_ARRAY, LIFESTYLE_ARRAY, SORT_VALUES_CREATED_DATE } from 'utils/constants';
import {
  buildURL,
  checkIfValueExist,
  formatMonthsToYears,
  printDate,
  printSerialNumbers,
  showImage,
  titleCaseIfExists,
} from 'utils/helper';
import { SMKLinks } from 'utils/links';

const stateSelector = createStructuredSelector({
  user: userSelector,
});
const DietPlansList = () => {
  const { user } = useSelector(stateSelector);

  const router = useRouter();
  const history = useHistory();
  toast.configure();

  const [dietPlansList, setDietPlansList] = useState([]);
  const [dietPlansListPagination, setDietPlansListPagination] = useState({});

  const defaultFilters = {
    active: { value: null, label: 'All' },
    deleted: { value: null, label: 'All' },
    name: '',
    gender: 0,
    lifestyle: '',
  };

  const [filters, setFilters] = useState(defaultFilters);

  const [getDietPlansListRequest, { loading: getDietPlansListLoading }] = useRequest({
    url: '/nutrition/diet-plan/admin/search',
    method: 'POST',
    onSuccess: (data) => {
      setDietPlansList(data?.results);
      setDietPlansListPagination(data?.pagination);
    },
  });

  const [updatedDietPlansRequest, { loading: updatedDietPlansLoading }] = useRequest({
    onSuccess: () => {
      handleChangePage(1);
      toast.success('Success! Diet Plan has been updated.');
    },
  });

  const setPageInUrl = (page) => {
    history.push({
      pathname: router.pathname,
      search: `?${new URLSearchParams({ page }).toString()}`,
    });
  };

  const handleChangePage = (page) => {
    setPageInUrl(page);

    getDietPlansListRequest({
      body: {
        page,
        size: 20,
        ...SORT_VALUES_CREATED_DATE,
        active: checkIfValueExist(filters.active?.value),
        deleted: checkIfValueExist(filters.deleted?.value),
        name: checkIfValueExist(filters.name?.trim()),
        gender: checkIfValueExist(filters.gender?.value),
        lifestyle: checkIfValueExist(filters.lifestyle?.value),
      },
    });
  };

  const applyFilters = () => {
    handleChangePage(1);
  };

  const clearFilters = () => {
    setPageInUrl(1);

    setFilters({ ...defaultFilters });
    getDietPlansListRequest({
      body: {
        page: 1,
        size: 20,
        ...SORT_VALUES_CREATED_DATE,
      },
    });
  };

  useEffect(() => {
    handleChangePage(parseInt(router?.query?.page, 10) || 1);
  }, []);

  const handleUpdateDietPlan = (data, key) => {
    if (key === 'deleted') {
      updatedDietPlansRequest({
        method: 'DELETE',
        url: `/nutrition/diet-plan/admin/${data?.id}`,
        body: {
          deleted: true,
          active: false,
        },
      });
    } else {
      updatedDietPlansRequest({
        method: 'PUT',
        url: `/nutrition/diet-plan/admin/${data?.id}`,
        body: {
          active: !data?.active,
          deleted: false,
        },
      });
    }
  };

  return (
    <div className="page-content">
      <Loader isActive={getDietPlansListLoading || updatedDietPlansLoading} />
      <Container fluid>
        <Breadcrumbs title="Diet Plans" breadcrumbItem={'Diet Plans List'} />

        <Card>
          <CardBody>
            <div className="mt-4">
              <div className="row">
                <div className="col-lg-2 mb-4">
                  <label className="form-label">Active</label>
                  <Select
                    options={ACTIVE_AND_DELETED_ARRAY}
                    value={filters.active}
                    onChange={(value) => {
                      setFilters({ ...filters, active: value });
                    }}
                  />
                </div>
                <div className="col-lg-2 mb-4">
                  <label className="form-label">Deleted</label>
                  <Select
                    options={ACTIVE_AND_DELETED_ARRAY}
                    value={filters.deleted}
                    onChange={(value) => setFilters({ ...filters, deleted: value })}
                  />
                </div>

                <div className="col-lg-3 mb-4">
                  <label className="form-label">Gender</label>
                  <Select
                    options={GENDER_ARRAY}
                    placeholder="Select gender..."
                    value={filters.gender}
                    onChange={(value) => setFilters({ ...filters, gender: value })}
                  />
                </div>
                <div className="col-lg-3 mb-4">
                  <label className="form-label">Lifestyle</label>
                  <Select
                    options={LIFESTYLE_ARRAY}
                    placeholder="Select Lifestyle..."
                    value={filters.lifestyle}
                    onChange={(value) => setFilters({ ...filters, lifestyle: value })}
                  />
                </div>

                <div className="col-lg-3 mb-4">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    placeholder="Enter name..."
                    value={filters.name}
                    onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <a className="btn btn-primary me-4" onClick={applyFilters}>
                Apply Filters
              </a>
              <a className="" onClick={clearFilters}>
                Clear
              </a>
            </div>
          </CardBody>
        </Card>

        <Row className="mb-2 align-items-center">
          <Col sm="4">
            <div className="search-box me-2 mb-2 d-inline-block">
              <div className="position-relative font-bold">
                {dietPlansListPagination?.totalCount || 0} Diet Plans Found
              </div>
            </div>
          </Col>
          {user.entityPermissions?.nutrition_diet_plan?.create && (
            <Col sm="8">
              <div className="text-sm-end">
                <Link
                  replace
                  className="btn btn-success btn-rounded waves-effect waves-light mb-2 me-2"
                  to={SMKLinks.DIET_PLAN_ADD}>
                  <i className="mdi mdi-plus me-1" />
                  Add Diet Plan
                </Link>
              </div>
            </Col>
          )}
        </Row>

        <Row>
          <Col lg="12">
            <div className="">
              <div className="table-responsive" style={{ minHeight: 300 }}>
                <Table className="project-list-table  table-borderless ">
                  <thead>
                    <tr className="table-warning">
                      <th scope="col">S.No.</th>
                      <th scope="col"></th>
                      <th scope="col">Name</th>
                      <th scope="col">Gender</th>
                      <th scope="col">Lifestyle</th>
                      <th scope="col">Min Age </th>
                      <th scope="col">Max Age </th>
                      <th scope="col">Created Date</th>
                      <th scope="col">System Defined</th>
                      <th scope="col">Active</th>
                      <th scope="col">Deleted</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {!isEmpty(dietPlansList) ? (
                      dietPlansList?.map((data, index) => {
                        return (
                          <tr key={index} className={data?.active ? '' : 'bg-danger text-emphasis-warning'}>
                            <td width={10}>{printSerialNumbers(dietPlansListPagination)[index]}</td>
                            <td width={40}>
                              {!isEmpty(data?.image) && (
                                <a href={showImage(data?.image?.thumbnailSmall)} target="_blank">
                                  <img
                                    src={showImage(data?.image?.thumbnailSmall)}
                                    style={{
                                      maxWidth: '30px',
                                      maxHeight: '30px',
                                    }}
                                  />
                                </a>
                              )}
                            </td>
                            <td>
                              {user.entityPermissions?.nutrition_diet_plan?.update ? (
                                <Link
                                  to={buildURL(SMKLinks.DIET_PLAN_EDIT, {
                                    id: data?.id,
                                  })}>
                                  {data?.name}
                                </Link>
                              ) : (
                                data?.name
                              )}
                            </td>

                            <td>{titleCaseIfExists(GENDER_ARRAY?.find((e) => e.value === data?.gender)?.label)}</td>
                            <td>
                              {titleCaseIfExists(LIFESTYLE_ARRAY?.find((e) => e.value === data?.lifestyle)?.label)}
                            </td>
                            <td>{formatMonthsToYears(data?.ageGroupMin)}</td>
                            <td>{formatMonthsToYears(data?.ageGroupMax)}</td>
                            <td>{printDate(data?.createdDate)}</td>
                            <td>
                              <Badge className={data?.isSystemDefined ? 'bg-success' : 'bg-danger'}>
                                {data?.isSystemDefined ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              <Badge className={data?.active ? 'bg-success' : 'bg-danger'}>
                                {data?.active ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              <Badge className={data?.deleted ? 'bg-success' : 'bg-danger'}>
                                {data?.deleted ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              {user.entityPermissions?.nutrition_diet_plan?.update && (
                                <UncontrolledDropdown>
                                  <DropdownToggle href="#" className="card-drop " tag="i">
                                    <i role="button" className="mdi mdi-dots-vertical font-size-18 " />
                                  </DropdownToggle>
                                  <DropdownMenu className="dropdown-menu-end">
                                    <DropdownItem onClick={() => handleUpdateDietPlan(data, 'active')}>
                                      <i
                                        className={`fas  ${
                                          data?.active ? 'fa-ban text-danger' : 'fa-power-off text-success'
                                        }   m-1 pe-3`}
                                      />
                                      {data?.active ? 'Disable' : 'Enable (Active)'}
                                    </DropdownItem>
                                    {!data?.deleted && (
                                      <DropdownItem onClick={() => handleUpdateDietPlan(data, 'deleted')}>
                                        <i className="fas fa-trash-alt text-danger m-1 pe-3" />
                                        {data?.deleted ? 'Undelete' : 'Delete'}
                                      </DropdownItem>
                                    )}

                                    <Divider className="my-2" />

                                    <Link
                                      to={buildURL(SMKLinks.DIET_PLAN_EDIT, {
                                        id: data?.id,
                                      })}>
                                      <DropdownItem>
                                        <i className="fas fa-pencil-alt text-success m-1 pe-3 " />
                                        Edit
                                      </DropdownItem>
                                    </Link>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <TableError />
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Col>
        </Row>
        <Paginate pageInfo={dietPlansListPagination} onChangePage={handleChangePage} />
      </Container>
    </div>
  );
};

export default DietPlansList;
